import {
  MULTILINGUAL_SPEAKER_LANGUAGE,
  type CaptionTtsCapability,
  type PublicCaptionLanguage,
  type PublicTargetCaptionLanguage,
} from "@kaleo/captions-core/languages";

const LANGUAGE_CODE_PATTERN = /^[a-z0-9]{1,8}(?:-[a-z0-9]{1,8})*$/i;

function readRequiredString(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized || null;
}

function readLanguageCode(value: unknown): string | null {
  const code = readRequiredString(value);
  return code && LANGUAGE_CODE_PATTERN.test(code) ? code : null;
}

function readStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;

  const values: string[] = [];
  for (const candidate of value) {
    const normalized = readRequiredString(candidate);
    if (!normalized) return null;
    if (!values.includes(normalized)) values.push(normalized);
  }
  return values;
}

function readOptionalLanguageCodeArray(
  value: unknown,
): string[] | null | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) return null;

  const codes: string[] = [];
  for (const candidate of value) {
    const code = readLanguageCode(candidate);
    if (!code) return null;
    if (!codes.includes(code)) codes.push(code);
  }
  return codes;
}

function readTtsCapabilities(
  value: unknown,
  productLanguageCode: string,
): CaptionTtsCapability[] | null {
  if (!Array.isArray(value)) return null;

  const capabilities: CaptionTtsCapability[] = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== "object") return null;

    const capability = candidate as {
      pipeline?: unknown;
      languageCodes?: unknown;
    };
    const pipeline = readRequiredString(capability.pipeline);
    const languageCodes = readStringArray(capability.languageCodes);
    if (
      pipeline !== "remote" ||
      !languageCodes ||
      languageCodes.length !== 1 ||
      languageCodes[0] !== productLanguageCode
    ) {
      return null;
    }

    capabilities.push({ pipeline, languageCodes });
  }
  return capabilities;
}

function readBaseLanguage(
  value: unknown,
): PublicCaptionLanguage | null {
  if (!value || typeof value !== "object") return null;

  const candidate = value as {
    code?: unknown;
    name?: unknown;
    emoji?: unknown;
    rtl?: unknown;
  };
  const code = readLanguageCode(candidate.code);
  const name = readRequiredString(candidate.name);
  const emoji = readRequiredString(candidate.emoji);

  if (
    !code ||
    !name ||
    !emoji ||
    (candidate.rtl !== undefined && typeof candidate.rtl !== "boolean")
  ) {
    return null;
  }

  return {
    code,
    name,
    emoji,
    ...(candidate.rtl === true ? { rtl: true } : {}),
  };
}

function readCatalog<T extends PublicCaptionLanguage>(
  value: unknown,
  readLanguage: (candidate: unknown) => T | null,
): T[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;

  const languages: T[] = [];
  const codes = new Set<string>();
  for (const candidate of value) {
    const language = readLanguage(candidate);
    if (!language) return null;

    if (codes.has(language.code)) return null;
    codes.add(language.code);
    languages.push(language);
  }
  return languages;
}

export function parseSpeakerLanguageCatalog(
  value: unknown,
): PublicCaptionLanguage[] | null {
  const languages = readCatalog(value, (candidate) => {
    const language = readBaseLanguage(candidate);
    if (!language || !candidate || typeof candidate !== "object") return null;

    const automaticDetectionLanguageCodes = readOptionalLanguageCodeArray(
      (candidate as { automaticDetectionLanguageCodes?: unknown })
        .automaticDetectionLanguageCodes,
    );
    if (automaticDetectionLanguageCodes === null) return null;

    return {
      ...language,
      ...(automaticDetectionLanguageCodes
        ? { automaticDetectionLanguageCodes }
        : {}),
    };
  });
  if (!languages) return null;

  const languageCodes = new Set(languages.map((language) => language.code));
  const automaticDetectionLanguageCodes = languages.find(
    (language) => language.code === MULTILINGUAL_SPEAKER_LANGUAGE,
  )?.automaticDetectionLanguageCodes;
  if (
    automaticDetectionLanguageCodes === undefined ||
    automaticDetectionLanguageCodes.length === 0 ||
    automaticDetectionLanguageCodes.some(
      (code) =>
        code === MULTILINGUAL_SPEAKER_LANGUAGE || !languageCodes.has(code),
    )
  ) {
    return null;
  }

  return languages;
}

export function parseTargetLanguageCatalog(
  value: unknown,
): PublicTargetCaptionLanguage[] | null {
  return readCatalog(value, (candidate) => {
    const language = readBaseLanguage(candidate);
    if (!language || !candidate || typeof candidate !== "object") return null;

    const nativeName = readRequiredString(
      (candidate as { nativeName?: unknown }).nativeName,
    );
    const ttsCapabilities = readTtsCapabilities(
      (candidate as { ttsCapabilities?: unknown }).ttsCapabilities,
      language.code,
    );
    return nativeName && ttsCapabilities
      ? { ...language, nativeName, ttsCapabilities }
      : null;
  });
}
